/**
 * Comparación entre dos fotos del miembro (v_my_progress / columnas extraídas de snapshots). Pura: la usan la
 * herramienta compare_snapshots del asesor y la figura "foto de entrada vs hoy" de Mi foto. Todo sale ya formateado.
 */
import type { Currency } from "../engine/types";
import { fmtMoney, fmtNum, fmtPct, fmtPts } from "../engine/format/esMX";

export interface ProgressRow {
  id?: string; created_at: string; label: string | null;
  net_worth: number | null; investable_net: number | null; savings_rate: number | null; freedom_pct: number | null; investable_return: number | null;
  emergency_funded_pct: number | null; idp: number | null; alloc_pres: number | null; alloc_inc: number | null; alloc_growth: number | null;
  objectives_funded: number | null; objectives_count: number | null;
}

export interface Delta { key: string; label: string; before: string; after: string; change: string; a: number | null; b: number | null; kind: "money" | "pct" | "num" }

const n = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number(v));

export function compareProgress(a: ProgressRow, b: ProgressRow, currency: Currency = "MXN") {
  const M = (v: number | null) => fmtMoney(v, currency);
  const items: [keyof ProgressRow, string, Delta["kind"]][] = [
    ["net_worth", "Patrimonio neto", "money"], ["investable_net", "Patrimonio invertible", "money"], ["savings_rate", "Tasa de ahorro", "pct"],
    ["freedom_pct", "% libertad financiera", "pct"], ["investable_return", "Rendimiento de lo invertible", "pct"], ["emergency_funded_pct", "Fondo de emergencia", "pct"],
    ["alloc_pres", "Preservación (% del portafolio)", "pct"], ["alloc_inc", "Ingreso (% del portafolio)", "pct"], ["alloc_growth", "Crecimiento (% del portafolio)", "pct"],
    ["idp", "IDP", "num"],
  ];
  const deltas: Delta[] = items.map(([key, label, kind]) => {
    const x = n(a[key]), y = n(b[key]);
    const fmt = (v: number | null) => (kind === "money" ? M(v) : kind === "pct" ? fmtPct(v, 1) : fmtNum(v, 1));
    const d = x === null || y === null ? null : y - x;
    const change = d === null ? "—" : kind === "money" ? `${d >= 0 ? "+" : "−"}${M(Math.abs(d))}` : kind === "pct" ? fmtPts(d) : `${d >= 0 ? "+" : ""}${fmtNum(d, 1)}`;
    return { key, label, before: fmt(x), after: fmt(y), change, a: x, b: y, kind };
  });
  const fecha = (f: string) => new Date(f).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
  const dias = Math.round((new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) / 864e5);
  return {
    from: { date: fecha(a.created_at), label: a.label }, to: { date: fecha(b.created_at), label: b.label }, days_between: dias,
    objectives_funded: { before: `${n(a.objectives_funded) ?? "—"} de ${n(a.objectives_count) ?? "—"}`, after: `${n(b.objectives_funded) ?? "—"} de ${n(b.objectives_count) ?? "—"}` },
    deltas,
  };
}
